import { useState, useEffect, useRef, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { RefreshCw } from 'lucide-react'
import { csrfHeaders } from '@/lib/csrf'
import type { Project } from '@/types/conversations'

interface LiveLogsSectionProps {
  project: Project
}

export function LiveLogsSection({ project }: LiveLogsSectionProps) {
  const { t } = useTranslation('conversations')
  const [serviceType, setServiceType] = useState<'web' | 'worker'>('web')
  const [lineCount, setLineCount] = useState(100)
  const [logs, setLogs] = useState<string[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const scrollRef = useRef<HTMLDivElement>(null)

  const hasWorker = project.deploy_tier === 'full_stack_with_workers'

  const fetchLogs = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const response = await fetch(
        `/projects/${project.id}/production/logs?service_type=${serviceType}&lines=${lineCount}`,
        { headers: csrfHeaders() }
      )
      const data = await response.json()
      if (!response.ok || data.error) {
        setError(data.error || t('live.sre.logs.error'))
      } else {
        setLogs(data.lines || [])
      }
    } catch {
      setError(t('live.sre.logs.error'))
    } finally {
      setLoading(false)
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps -- t excluded: only used for error fallback, not worth refetching on language change
  }, [project.id, serviceType, lineCount])

  useEffect(() => {
    fetchLogs()
  }, [fetchLogs])

  useEffect(() => {
    if (scrollRef.current) {
      scrollRef.current.scrollTop = scrollRef.current.scrollHeight
    }
  }, [logs])

  return (
    <div className="flex flex-col h-full">
      {/* Controls */}
      <div className="flex items-center gap-2 px-4 py-2 border-b border-border">
        <div className="flex gap-1 bg-muted rounded-md p-0.5">
          <button
            className={`px-2.5 py-1 text-xs rounded font-medium transition-colors ${
              serviceType === 'web'
                ? 'bg-background shadow-sm text-foreground'
                : 'text-muted-foreground hover:text-foreground'
            }`}
            onClick={() => setServiceType('web')}
          >
            {t('live.sre.logs.api')}
          </button>
          {hasWorker && (
            <button
              className={`px-2.5 py-1 text-xs rounded font-medium transition-colors ${
                serviceType === 'worker'
                  ? 'bg-background shadow-sm text-foreground'
                  : 'text-muted-foreground hover:text-foreground'
              }`}
              onClick={() => setServiceType('worker')}
            >
              {t('live.sre.logs.worker')}
            </button>
          )}
        </div>

        <select
          value={lineCount}
          onChange={(e) => setLineCount(Number(e.target.value))}
          className="text-xs border rounded px-2 py-1 bg-background"
        >
          {[50, 100, 200, 500].map((n) => (
            <option key={n} value={n}>
              {n} {t('live.sre.logs.lines')}
            </option>
          ))}
        </select>

        <div className="flex-1" />

        <Button variant="ghost" size="sm" onClick={fetchLogs} disabled={loading}>
          <RefreshCw className={`h-3.5 w-3.5 mr-1 ${loading ? 'animate-spin' : ''}`} />
          {t('live.sre.logs.refresh')}
        </Button>
      </div>

      {/* Log output */}
      <div
        ref={scrollRef}
        className="flex-1 overflow-auto bg-gray-950 p-3 font-mono text-xs leading-5"
      >
        {loading && logs.length === 0 ? (
          <div className="text-gray-500 text-center py-8">{t('live.sre.logs.loading')}</div>
        ) : error ? (
          <div className="text-red-400 text-center py-8">{error}</div>
        ) : logs.length === 0 ? (
          <div className="text-gray-500 text-center py-8">{t('live.sre.logs.noLogs')}</div>
        ) : (
          logs.map((line, i) => (
            <div key={i} className="text-green-400 whitespace-pre-wrap break-all hover:bg-gray-900/50">
              {line}
            </div>
          ))
        )}
      </div>
    </div>
  )
}
